import { Card, CardContent, Typography, Box, MenuItem, Select } from "@mui/material";
import GenericTable from "../../Components/Back-End/Table";
import { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import SectionCard from "../../Components/Back-End/SectionCard";

export function CoachesTable() {
    const [validationErrors, setValidationErrors] = useState({});
    const [coachTypes, setCoachTypes] = useState([]);

    useEffect(() => {
        async function fetchCoachTypes() {
            try {
                const res = await fetch('/api/coachtypes/GetCoachTypes');
                if (!res.ok) throw new Error('Failed to fetch coach types');
                const data = await res.json();
                //console.log("Fetched coach types:", data); // ✅ Check output
                setCoachTypes(data);
            } catch (error) {
                console.error(error);
            }
        }

        fetchCoachTypes();
    }, []);



    const columns = [
        {
            accessorKey: "id",
            header: "ID",
            enableEditing: false,
        },
        {
            accessorKey: "imageUrl",
            header: "Image",
            Edit: ({ cell, row }) => {
                const [previewUrl, setPreviewUrl] = useState(null);
                const [error, setError] = useState(""); // to show error message
                const filename = cell.getValue();
                const existingUrl = filename ? `fsc-images/coaches/${filename}` : null;

                const handleFileChange = async (event) => {
                    const file = event.target.files[0];
                    if (file) {
                        // Check if file exceeds 5 MB
                        if (file.size > 5 * 1024 * 1024) {
                            setError("File size must be less than 5 MB");
                            event.target.value = ""; // reset the input
                            setPreviewUrl(null); // clear preview
                            return;
                        }

                        setError(""); // clear previous errors

                        const reader = new FileReader();
                        reader.onloadend = () => {
                            setPreviewUrl(reader.result); // base64 preview
                        };
                        reader.readAsDataURL(file);

                        // Store the actual file on the row to submit later
                        row._valuesCache["imageFile"] = file;
                    }
                };

                return (
                    <Box display="flex" alignItems="center" gap={2}>
                        {(previewUrl || existingUrl) && (
                            <img
                                src={previewUrl || existingUrl}
                                alt="Preview"
                                style={{
                                    width: "80px",
                                    height: "80px",
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    border: "2px solid #ccc",
                                    flexShrink: 0,
                                    marginTop: "1rem",
                                    marginBottom: "1rem"
                                }}
                            />
                        )}
                        <Box>
                            <label style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                                Upload Image
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    style={{ display: "block", marginTop: "0.25rem" }}
                                />
                            </label>
                            {error && (
                                <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{ display: "block", marginTop: "0.25rem" }}
                                >
                                    {error}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            },
            Cell: ({cell}) => {
                const filename = cell.getValue();
                const imagePath = filename ? `fsc-images/coaches/${filename}` : '';
                return (
                    <img
                        src={imagePath}
                        alt="Coach"
                        style={{
                            width: "60px",
                            height: "60px",
                            objectFit: "cover",
                            borderRadius: "50%",
                            border: "2px solid #ddd",
                        }}
                    />
                );
            }
        },
        {
            accessorKey: "name",
            header: "Name",
            validate: (value) => {
                if (!value || value.trim() === "") return "Coach name is required.";
            },
            muiEditTextFieldProps: () => ({
                required: true,
                error: !!validationErrors.name,
                helperText: validationErrors.name ?? '',
                onChange: () => setValidationErrors(prev => ({ ...prev, name: undefined })),
            }),
        },
        {
            accessorKey: "coachTypeId",
            header: "Coach Type",
            editVariant: "select",
            editSelectOptions: coachTypes.map(x => ({
                label: x.typeName,
                value: x.id
            })),
            muiEditTextFieldProps: () => ({
                required: true,
                select: true,
                error: !!validationErrors.coachTypeId,
                helperText: validationErrors.coachTypeId ?? '',
                onChange: () => setValidationErrors(prev => ({ ...prev, coachTypeId: undefined })),
            }),
            validate: (value) => {
                if (value === undefined || value === null || value === "") {
                    return "Coach type is required.";
                }
            },
            Cell: ({ cell }) => {
                const coachTypeId = cell.getValue();
                const coachType = coachTypes.find(x => x.id === coachTypeId);
                return coachType ? coachType.typeName : coachTypeId;
            }
        },
        {
            accessorKey: "jobTitle",
            meta: { hideInTable: true },
            header: "Job Title",
            muiEditTextFieldProps: () => ({
                error: !!validationErrors.jobTitle,
                helperText: validationErrors.jobTitle ?? '',
                onChange: () => setValidationErrors(prev => ({ ...prev, jobTitle: undefined })),
            }),
        },
        {
            accessorKey: "email",
            header: "Email",
            meta: { hideInTable: true },
            muiEditTextFieldProps: () => ({
                type: "email",
                error: !!validationErrors.email,
                helperText: validationErrors.email ?? '',
                onChange: () => setValidationErrors(prev => ({ ...prev, email: undefined })),
            }),
        },
        {
            accessorKey: "descriptionHtml",
            header: "Description",
            meta: { hideInTable: true },
            Edit: ({ cell, column, row, table }) => {
                const value = cell.getValue() || "";

                const handleChange = (content, delta, source, editor) => {
                    row._valuesCache[column.id] = editor.getHTML();
                    table.setEditingRow(row);
                };

                return (
                    <Box sx={{
                        '& .ql-tooltip': {
                            position: 'absolute',
                            left: '50% !important',
                            transform: 'translateX(-50%)',
                            overflow: 'visible',
                        },
                        '& .ql-container': {
                            overflow: 'visible',
                        },
                    }}>
                        <Typography sx={{ fontWeight: 300, marginTop: '0.5rem', fontSize: '0.8rem' }}>
                            Coach Bio
                        </Typography>

                        <ReactQuill
                            theme="snow"
                            value={value}
                            onChange={handleChange}
                            modules={{
                                toolbar: [
                                    ['bold', 'underline'],
                                    [{ color: [] }, { background: [] }],
                                    ['link'],
                                    [{ list: 'ordered' }, { list: 'bullet' }]
                                ],
                            }}
                            style={{ height: 250, marginBottom: '4.5rem' }}
                        />
                    </Box>
                );
            },
            Cell: ({ cell }) => (
                <div dangerouslySetInnerHTML={{ __html: cell.getValue() }} />
            ),
        },
        {
            accessorKey: "sortOrder",
            header: "Sort Order",
            muiEditTextFieldProps: () => ({
                type: "number",
                required: true,
                error: !!validationErrors.sortOrder,
                helperText: validationErrors.sortOrder ?? '',
                onChange: () => setValidationErrors(prev => ({ ...prev, sortOrder: undefined })),
            }),
            validate: (value) => {
                if (value === undefined || value === null || value === "") {
                    return "Sort order is required.";
                }
                if (isNaN(value)) {
                    return "Sort order must be a number.";
                }
                if (Number(value) < 0) {
                    return "Sort order must be greater than or equal to 0.";
                }
            },
        },
        {
            accessorKey: "isActive",
            header: "Active",
            editVariant: "select",
            editSelectOptions: [
                { label: "Yes", value: true },
                { label: "No", value: false },
            ],
            muiEditTextFieldProps: () => ({
                select: true,
                required: true,
                defaultValue: true,
                error: !!validationErrors.isActive,
                helperText: validationErrors.isActive ?? '',
                onChange: () => setValidationErrors(prev => ({ ...prev, isActive: undefined })),
            }),
            validate: (value) => {
                if (value === undefined || value === null || value === "") {
                    return "Active status is required.";
                }
            },
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value ? "Yes" : "No";
            },
        },
    ];

    const columnVisibility = columns.reduce((acc, col) => {
        if (col.meta?.hideInTable) {
            acc[col.accessorKey] = false;
        }
        return acc;
    }, {});

    return (
        <SectionCard title="Coaches" subtitle="Manage your coaches below">
            <GenericTable
                columns={columns}
                itemName="Coach"
                createApi="/api/coaches/save"
                readApi="/api/coaches/GetCoaches"
                updateApi="/api/coaches/save"
                deleteApi="/api/coaches/delete"
                columnVisibility={columnVisibility}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
                publicSitePage="coach"
            />
        </SectionCard>
    );



}
